import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaBox, FaCalendarAlt, FaHashtag, FaInfoCircle } from 'react-icons/fa';
import ProductService from '../services/ProductService';

const ProductBatchInfo = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [batches, setBatches] = useState([]);
    const [product, setProduct] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const productService = new ProductService();
                
                // Fetch both batches and product details in parallel
                const [batchResult, productResult] = await Promise.all([
                    productService.fetchProductBatches(id),
                    productService.fetchProductDetails(id)
                ]);

                if (batchResult.status) {
                    setBatches(batchResult.data);
                } else {
                    console.error('Batch fetch error:', batchResult.message);
                }

                if (productResult && productResult.length > 0) {
                    setProduct(productResult[0]);
                }
                
                if (!batchResult.status && (!productResult || productResult.length === 0)) {
                    setError('Failed to load product and batch information.');
                }

            } catch (err) {
                setError('An error occurred while fetching information.');
                console.error(err);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [id]);

    return (
        <div className="container py-5">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <button 
                    className="btn btn-link text-success p-0 d-flex align-items-center text-decoration-none fw-bold" 
                    onClick={() => navigate(-1)}
                >
                    <FaArrowLeft className="me-2" /> Back to Product
                </button>
                <span className="badge bg-success-soft text-success px-3 py-2" style={{ backgroundColor: 'rgba(25, 135, 84, 0.1)', borderRadius: '10px' }}>
                    Batch Records
                </span>
            </div>

            <div className="card border-0 shadow-lg mb-4" style={{ borderRadius: '15px', overflow: 'hidden' }}>
                <div className="card-header bg-success text-white py-3 border-0">
                    <h5 className="mb-0 d-flex align-items-center fw-bold">
                        <FaInfoCircle className="me-2" /> Product Batch Information
                    </h5>
                </div>
                {product && (
                    <div className="card-body border-bottom bg-light py-4 px-4">
                        <div className="row align-items-center">
                            <div className="col-md-auto text-center mb-3 mb-md-0">
                                {product.images && product.images[0] && (
                                    <div className="bg-white rounded p-2 shadow-sm d-inline-block" style={{ border: '1px solid #eee' }}>
                                        <img 
                                            src={product.images[0]} 
                                            alt={product.product_name} 
                                            className="img-fluid" 
                                            style={{ maxHeight: '80px', width: '80px', objectFit: 'contain' }}
                                        />
                                    </div>
                                )}
                            </div>
                            <div className="col-md ps-md-4">
                                <h3 className="h5 text-success fw-bold mb-0" style={{ letterSpacing: '0.5px' }}>
                                    {product.product_name}
                                </h3>
                                <p className="text-muted small mb-0 mt-1">Detailed batch tracking and information</p>
                            </div>
                        </div>
                    </div>
                )}
                <div className="card-body p-4">
                    {loading ? (
                        <div className="text-center py-5">
                            <div className="spinner-border text-success" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                            <p className="mt-3 text-muted">Fetching batch details...</p>
                        </div>
                    ) : error ? (
                        <div className="alert alert-danger" role="alert">
                            {error}
                        </div>
                    ) : batches.length === 0 ? (
                        <div className="text-center py-5">
                            <FaBox size={50} className="text-muted mb-3" />
                            <p className="text-muted">No batch information available for this product.</p>
                        </div>
                    ) : (
                        <div className="table-responsive">
                            <table className="table table-hover align-middle">
                                <thead className="table-light">
                                    <tr>
                                        {batches.length > 0 && Object.keys(batches[0])
                                            .filter(key => !['batch_id', 'product_id'].includes(key))
                                            .map((key) => (
                                                <th key={key} className="text-capitalize">
                                                    {key.replace(/_/g, ' ')}
                                                </th>
                                            ))
                                        }
                                    </tr>
                                </thead>
                                <tbody>
                                    {batches.map((batch, index) => (
                                        <tr key={batch.batch_id || index}>
                                            {Object.keys(batch)
                                                .filter(key => !['batch_id', 'product_id'].includes(key))
                                                .map((key) => (
                                                    <td key={key}>
                                                        {key === 'batch_no' ? (
                                                            <span className="badge bg-light text-dark border p-2">
                                                                {batch[key]}
                                                            </span>
                                                        ) : key === 'expiry' ? (
                                                            <span className="text-danger fw-bold">{batch[key]}</span>
                                                        ) : (
                                                            batch[key]
                                                        )}
                                                    </td>
                                                ))
                                            }
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            <style>{`
                .batch-table-container {
                    transition: transform 0.3s ease;
                }
                .batch-table tr:hover {
                    background-color: rgba(25, 135, 84, 0.05) !important;
                }
            `}</style>
        </div>
    );
};

export default ProductBatchInfo;
